import { useCallback, useEffect, useState } from "react";
import { type Editor } from "@tiptap/react";

// --- Hooks ---
import { useTiptapEditor } from "app/components/TextEditor/hooks/use-tiptap-editor";

// --- Lib ---
import { isExtensionAvailable } from "app/components/TextEditor/lib/tiptap-utils";

// --- Icons ---
import { TableIcon } from "app/components/TextEditor/components/tiptap-icons/table-icon";

export interface InsertTableOptions {
  rows: number;
  cols: number;
  withHeaderRow: boolean;
}

export interface UseInsertTableConfig {
  editor?: Editor | null;
  rows?: number;
  cols?: number;
  withHeaderRow?: boolean;
  hideWhenUnavailable?: boolean;
  onInserted?: () => void;
}

export const INSERT_TABLE_SHORTCUT_KEY = "mod+alt+t";

export const INSERT_TABLE_DEFAULT_OPTIONS: InsertTableOptions = {
  rows: 3,
  cols: 3,
  withHeaderRow: true,
};

export function canInsertTable(
  editor: Editor | null,
  options: InsertTableOptions,
): boolean {
  if (!editor || !editor.isEditable) return false;
  if (!isExtensionAvailable(editor, "table")) return false;

  return editor.can().insertTable(options);
}

export function isTableActive(editor: Editor | null): boolean {
  if (!editor || !editor.isEditable) return false;
  return editor.isActive("table");
}

export function insertTable(
  editor: Editor | null,
  options: InsertTableOptions,
): boolean {
  if (!editor || !editor.isEditable) return false;
  if (!canInsertTable(editor, options)) return false;

  return editor.chain().focus().insertTable(options).run();
}

export function shouldShowButton(props: {
  editor: Editor | null;
  hideWhenUnavailable: boolean;
  options: InsertTableOptions;
}): boolean {
  const { editor, hideWhenUnavailable, options } = props;

  if (!editor || !editor.isEditable) return false;
  if (!hideWhenUnavailable) return true;
  if (!isExtensionAvailable(editor, "table")) return false;

  return canInsertTable(editor, options);
}

export function useInsertTable(config: UseInsertTableConfig = {}) {
  const {
    editor: providedEditor,
    rows = INSERT_TABLE_DEFAULT_OPTIONS.rows,
    cols = INSERT_TABLE_DEFAULT_OPTIONS.cols,
    withHeaderRow = INSERT_TABLE_DEFAULT_OPTIONS.withHeaderRow,
    hideWhenUnavailable = false,
    onInserted,
  } = config;

  const { editor } = useTiptapEditor(providedEditor);
  const [isVisible, setIsVisible] = useState<boolean>(true);

  const options: InsertTableOptions = { rows, cols, withHeaderRow };
  const canInsert = canInsertTable(editor, options);
  const isActive = isTableActive(editor);

  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = () => {
      setIsVisible(
        shouldShowButton({
          editor,
          hideWhenUnavailable,
          options: { rows, cols, withHeaderRow },
        }),
      );
    };

    handleSelectionUpdate();

    editor.on("selectionUpdate", handleSelectionUpdate);
    editor.on("update", handleSelectionUpdate);

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate);
      editor.off("update", handleSelectionUpdate);
    };
  }, [editor, hideWhenUnavailable, rows, cols, withHeaderRow]);

  const handleInsertTable = useCallback(() => {
    if (!editor) return false;

    const success = insertTable(editor, { rows, cols, withHeaderRow });
    if (success) {
      onInserted?.();
    }
    return success;
  }, [editor, onInserted, rows, cols, withHeaderRow]);

  return {
    isVisible,
    isActive,
    canInsert,
    handleInsertTable,
    label: "Insert table",
    shortcutKeys: INSERT_TABLE_SHORTCUT_KEY,
    Icon: TableIcon,
  };
}
